<script setup lang="ts">
import type { Dictionary } from "@/types";

import { computed, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import { VTextField } from "vuetify/components";

import { userIcon } from "@/utilities/discord";

const props = defineProps<{
  modelValue: boolean;
  currentDictionary: Dictionary | undefined;
  dictionaries: Dictionary[];
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "save", payload: { before: string; after: string }): void;
}>();

const { mobile } = useDisplay();

const beforeRef = ref<InstanceType<typeof VTextField> | null>(null);
const before = ref("");
const after = ref("");
const isError = ref(false);

const rules = computed(() => ({
  required: (value: string) => value?.trim().length > 0 || "必須です",
  uniq: (value: string) => {
    return (
      props.dictionaries.every(({ id, before }) => {
        return before !== value.trim() || id === props.currentDictionary?.id;
      }) || "既に登録済みです"
    );
  },
  asciiLength: (value: string) =>
    !value.match(/^[\x20-\x7e]$/) || "半角文字は2文字以上にしてください",
}));

const isChanged = computed(() => {
  return (
    before.value !== props.currentDictionary?.before ||
    after.value !== props.currentDictionary?.after
  );
});
const canSave = computed(() => !isError.value && isChanged.value);

const closeDialog = () => emit("update:modelValue", false);

const reset = () => {
  before.value = props.currentDictionary?.before || "";
  after.value = props.currentDictionary?.after || "";
};

const validate = async () => {
  if (!beforeRef.value) return;

  const errors = await beforeRef.value.validate();
  isError.value = errors.length > 0;
};

const save = async () => {
  await validate();
  if (!canSave.value) return;

  emit("save", { before: before.value, after: after.value });
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) return;

    before.value = props.currentDictionary?.before || "";
    after.value = props.currentDictionary?.after || "";
    isError.value = false;
  },
);
</script>

<template>
  <v-dialog
    :model-value="props.modelValue"
    class="p-dictionary__dialog"
    :class="{ 'is-mobile': mobile }"
    :fullscreen="mobile"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card>
      <v-toolbar theme="dark">
        <v-btn
          icon
          @click="closeDialog"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>
          {{ props.currentDictionary ? "辞書更新" : "辞書追加" }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn
            class="mr-1 text-black"
            color="cancel"
            tabindex="4"
            :disabled="!isChanged"
            @click="reset"
            >戻す</v-btn
          >
          <v-btn
            color="secondary"
            tabindex="3"
            :disabled="!canSave"
            @click="save"
            >保存</v-btn
          >
        </v-toolbar-items>
      </v-toolbar>
      <v-container>
        <div class="mb-5">
          ※変換前に半角文字のみを登録する場合は2文字以上の指定が必要です（日本語や絵文字などは1文字だけで登録可能です）
        </div>

        <v-text-field
          ref="beforeRef"
          v-model="before"
          :rules="[rules.required, rules.uniq, rules.asciiLength]"
          class="mb-2"
          label="変換前"
          autofocus
          autocomplete="new-password"
          aria-autocomplete="none"
          tabindex="1"
          validate-on="blur"
          @blur="validate"
        />
        <v-text-field
          v-model="after"
          class="mb-10"
          label="変換後"
          autocomplete="new-password"
          tabindex="2"
        />
        <div v-if="props.currentDictionary?.user">
          <span class="mr-2">追加したユーザー</span>
          <div class="d-flex align-center mt-2 ml-2">
            <v-avatar
              :color="props.currentDictionary.user.accent_color || 'primary'"
              size="32"
              class="mr-1"
            >
              <v-img
                :src="userIcon(props.currentDictionary.user)"
                width="32"
              />
            </v-avatar>
            <div class="ml-1 text-left">
              <div class="text-subtitle-2">
                {{ props.currentDictionary.user.nick || props.currentDictionary.user.name }}
              </div>
              <div class="text-caption text-grey">
                {{ props.currentDictionary.user.username }}
              </div>
            </div>
          </div>
        </div>
      </v-container>
    </v-card>
  </v-dialog>
</template>
